// @adr SPARK-0055-document-displays-use-one-public-boundary@3: Document embedding belongs inside the shared viewer, never in a feature page.
import { lazy, Suspense, useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Button } from "@/components/design/Button";
import { prepareDocumentPreview, type DocumentResource, type PreparedDocumentPreview } from "@/lib/document-resource";
import { downloadBlob } from "@/lib/file-download";
import { userErrorMessage } from "@/lib/user-error";

const PdfDocumentPreview = lazy(() => import("./PdfDocumentPreview"));

export function DocumentPreview({ resource, onClose }: { resource: DocumentResource; onClose?: () => void }) {
  const intl = useIntl();
  const [state, setState] = useState<{ resource: DocumentResource; preview?: PreparedDocumentPreview; error?: unknown }>();
  const [attempt, setAttempt] = useState(0);
  const [zoom, setZoom] = useState(100);
  useEffect(() => {
    let active = true;
    let preview: PreparedDocumentPreview | undefined;
    void prepareDocumentPreview(resource).then(value => {
      if (!active) { value.dispose(); return; }
      preview = value;
      setState({ resource, preview });
    }).catch(error => { if (active) setState({ resource, error }); });
    return () => { active = false; preview?.dispose(); };
  }, [resource, attempt]);
  const preview = state?.resource === resource ? state.preview : undefined;
  const error = state?.resource === resource ? state.error : undefined;
  const html = preview?.html?.replace("body{margin:0", `body{zoom:${zoom / 100};margin:0`);
  return <>
    <div className="mb-3 flex flex-wrap items-center justify-end gap-2">
      {(preview?.html || preview?.pdf) && <>
        <Button id="document-preview-zoom-out" variant="secondary" size="sm" disabled={zoom <= 50} onClick={() => setZoom(z => z - 25)} aria-label={intl.formatMessage({ id: "documents.preview.zoomOut" })}>−</Button>
        <span className="text-smycles-meta">{zoom}%</span>
        <Button id="document-preview-zoom-in" variant="secondary" size="sm" disabled={zoom >= 200} onClick={() => setZoom(z => z + 25)} aria-label={intl.formatMessage({ id: "documents.preview.zoomIn" })}>+</Button>
      </>}
      {preview && <Button id="document-preview-download" variant="secondary" size="sm" onClick={() => downloadBlob(preview.blob, resource.name)}><FormattedMessage id="service.docPreviewCard.download" /></Button>}
      {onClose && <Button id="document-preview-close" variant="secondary" size="sm" onClick={onClose}><FormattedMessage id="common.close" /></Button>}
    </div>
    {error ? <div role="alert" className="space-y-3 text-smycles-body"><p>{userErrorMessage(intl, error)}</p><Button id="document-preview-retry" variant="secondary" onClick={() => { setState(undefined); setAttempt(a => a + 1); }}><FormattedMessage id="common.tryAgain" /></Button></div>
      : !preview ? <p role="status" className="py-8 text-smycles-body"><FormattedMessage id="common.loading" /></p>
      : preview.pdf ? <Suspense fallback={<p role="status"><FormattedMessage id="common.loading" /></p>}><PdfDocumentPreview key={resource.name} blob={preview.blob} zoom={zoom} /></Suspense>
      : html || preview.url ? <iframe id="document-preview-frame" title={resource.name} srcDoc={html} src={preview.url} sandbox="" className="h-[65vh] w-full rounded border border-ink/10 bg-page" />
      : <p id="document-preview-unavailable" className="py-8 text-smycles-body"><FormattedMessage id="documents.preview.unavailable" /></p>}
  </>;
}
